import type { CheckItem, Section } from '../types';
import {
  audioDirForMarkdown,
  audioDirsForPageSlug,
  audioRepoPath,
} from '../utils/itemAudio';
import {
  videoDirForMarkdown,
  videoDirsForPageSlug,
  videoRepoPath,
} from '../utils/itemVideo';
import { videoThumbnailRepoPath } from '../utils/videoThumbnail';
import { committedVideoRepoFilename } from '../utils/videoFilenameEdit';
import {
  imageDirsForPageSlug,
  imageRepoPath,
  imagesDirForMarkdown,
  pageSlugFromMarkdown,
} from '../utils/itemImages';
import { parentDirOfMarkdown } from '../utils/markdownPaths';
import { thumbnailRepoPath } from '../utils/imageThumbnail';
import { queueOrphanedAudioForItem } from './ItemAudioSync';
import { queueOrphanedVideoForItem } from './ItemVideoSync';
import { queueOrphanedImagesForItem } from './ItemImageSync';
import type { GitHubService } from './GitHubService';
import type { DatabaseFile } from '../types';

async function copyBlobIfPresent(github: GitHubService, fromPath: string, toPath: string): Promise<void> {
  const fromSha = await github.getBlobSha(fromPath);
  if (!fromSha) return;
  const { bytes } = await github.readBinary(fromPath);
  const toSha = await github.getBlobSha(toPath);
  await github.writeBinary(toPath, bytes, toSha ?? '', `Copy ${fromPath.split('/').pop()}`);
}

async function migratePageAssetDir(
  github: GitHubService,
  oldMdPath: string,
  newMdPath: string,
  kind: 'images' | 'audio' | 'video'
): Promise<void> {
  const newDir =
    kind === 'images'
      ? imagesDirForMarkdown(newMdPath)
      : kind === 'audio'
        ? audioDirForMarkdown(newMdPath)
        : videoDirForMarkdown(newMdPath);
  const oldParent = parentDirOfMarkdown(oldMdPath);
  const oldSlug = pageSlugFromMarkdown(oldMdPath);
  const sourceDirs =
    kind === 'images'
      ? imageDirsForPageSlug(oldParent, oldSlug)
      : kind === 'audio'
        ? audioDirsForPageSlug(oldParent, oldSlug)
        : videoDirsForPageSlug(oldParent, oldSlug);
  const seen = new Set<string>();
  const label = kind === 'images' ? 'image' : kind === 'audio' ? 'audio' : 'video';

  for (const oldDir of sourceDirs) {
    const blobs = await github.listBlobPathsUnderPrefix(`${oldDir}/`);
    for (const oldPath of blobs) {
      if (seen.has(oldPath)) continue;
      seen.add(oldPath);
      const suffix = oldPath.slice(oldDir.length);
      const newPath = `${newDir}${suffix}`;
      await copyBlobIfPresent(github, oldPath, newPath);
      const sha = await github.getBlobSha(oldPath);
      if (sha) {
        await github.deleteFile(oldPath, sha, `Move ${label} with page ${newMdPath.split('/').pop()}`);
      }
    }
  }
}

/** Move every blob under `{page}/_images/` and `{page}/_audio/` when the markdown path changes. */
export async function migratePageAssets(
  github: GitHubService,
  oldMdPath: string,
  newMdPath: string
): Promise<void> {
  if (oldMdPath === newMdPath) return;
  await migratePageAssetDir(github, oldMdPath, newMdPath, 'images');
  await migratePageAssetDir(github, oldMdPath, newMdPath, 'audio');
  await migratePageAssetDir(github, oldMdPath, newMdPath, 'video');
}

/** Copy committed item images (and thumbnails) to another page's image folder. */
export async function copySectionImagesToPage(
  github: GitHubService,
  fromMdPath: string,
  toMdPath: string,
  section: Section
): Promise<void> {
  if (fromMdPath === toMdPath) return;
  const seen = new Set<string>();
  for (const item of section.items) {
    for (const filename of item.images) {
      if (seen.has(filename)) continue;
      seen.add(filename);
      await copyBlobIfPresent(github, imageRepoPath(fromMdPath, filename), imageRepoPath(toMdPath, filename));
      await copyBlobIfPresent(
        github,
        thumbnailRepoPath(fromMdPath, filename),
        thumbnailRepoPath(toMdPath, filename)
      );
    }
  }
}

export function queueSectionImagesAsOrphaned(file: DatabaseFile, section: Section): void {
  for (const item of section.items) {
    queueOrphanedImagesForItem(file, item);
    queueOrphanedAudioForItem(file, item);
    queueOrphanedVideoForItem(file, item);
  }
}

export function cloneItem(item: CheckItem): CheckItem {
  return {
    raw: item.raw,
    text: item.text,
    date: item.date,
    state: item.state,
    images: [...item.images],
    pendingImages: [...item.pendingImages],
    imagesToDelete: [],
    audio: [...item.audio],
    pendingAudio: [...item.pendingAudio],
    audioToDelete: [],
    video: [...item.video],
    pendingVideo: [...item.pendingVideo],
    videoToDelete: [],
    videoRenames: [...(item.videoRenames ?? [])],
  };
}

/** Copy one item's committed images (and thumbnails) to another page's image folder. */
export async function copyItemImagesToPage(
  github: GitHubService,
  fromMdPath: string,
  toMdPath: string,
  item: CheckItem
): Promise<void> {
  const oneItemSection: Section = {
    title: '',
    level: 2,
    items: [item],
    prose: [],
  };
  await copySectionImagesToPage(github, fromMdPath, toMdPath, oneItemSection);
  await copySectionAudioToPage(github, fromMdPath, toMdPath, oneItemSection);
  await copySectionVideoToPage(github, fromMdPath, toMdPath, oneItemSection);
}

/** Copy committed item audio clips to another page's audio folder. */
export async function copySectionAudioToPage(
  github: GitHubService,
  fromMdPath: string,
  toMdPath: string,
  section: Section
): Promise<void> {
  if (fromMdPath === toMdPath) return;
  const seen = new Set<string>();
  for (const item of section.items) {
    for (const filename of item.audio) {
      if (seen.has(filename)) continue;
      seen.add(filename);
      await copyBlobIfPresent(github, audioRepoPath(fromMdPath, filename), audioRepoPath(toMdPath, filename));
    }
  }
}

/** Copy one item's committed audio clips to another page's audio folder. */
export async function copyItemAudioToPage(
  github: GitHubService,
  fromMdPath: string,
  toMdPath: string,
  item: CheckItem
): Promise<void> {
  const oneItemSection: Section = {
    title: '',
    level: 2,
    items: [item],
    prose: [],
  };
  await copySectionAudioToPage(github, fromMdPath, toMdPath, oneItemSection);
}

/** Copy committed item video clips to another page's video folder. */
export async function copySectionVideoToPage(
  github: GitHubService,
  fromMdPath: string,
  toMdPath: string,
  section: Section
): Promise<void> {
  if (fromMdPath === toMdPath) return;
  const seen = new Set<string>();
  for (const item of section.items) {
    for (const filename of item.video) {
      if (seen.has(filename)) continue;
      seen.add(filename);
      const sourceName = committedVideoRepoFilename(item, filename);
      await copyBlobIfPresent(
        github,
        videoRepoPath(fromMdPath, sourceName),
        videoRepoPath(toMdPath, filename)
      );
      await copyBlobIfPresent(
        github,
        videoThumbnailRepoPath(fromMdPath, sourceName),
        videoThumbnailRepoPath(toMdPath, filename)
      );
    }
  }
}

/** Copy one item's committed video clips to another page's video folder. */
export async function copyItemVideoToPage(
  github: GitHubService,
  fromMdPath: string,
  toMdPath: string,
  item: CheckItem
): Promise<void> {
  const oneItemSection: Section = {
    title: '',
    level: 2,
    items: [item],
    prose: [],
  };
  await copySectionVideoToPage(github, fromMdPath, toMdPath, oneItemSection);
}

export function cloneSection(section: Section): Section {
  return {
    title: section.title,
    level: section.level,
    prose: [...section.prose],
    items: section.items.map(item => cloneItem(item)),
    ...(section.itemSort ? { itemSort: section.itemSort } : {}),
  };
}
